/**
 * useServiceWorker — manual SW lifecycle hook
 *
 * Exposes:
 *  - supported:   browser supports SW
 *  - registered:  a SW is currently registered
 *  - updateReady: a waiting SW is ready to activate
 *  - checkForUpdate(): call reg.update() and refresh state
 *  - applyUpdate():    send SKIP_WAITING and reload
 *  - forceUpdate():    unregister + clear caches + reload  (the "nuke" button)
 */

import { useCallback, useEffect, useState } from 'react';

interface ServiceWorkerState {
  supported: boolean;
  registered: boolean;
  updateReady: boolean;
  checkForUpdate: () => Promise<void>;
  applyUpdate: () => void;
  forceUpdate: () => Promise<void>;
}

export function useServiceWorker(): ServiceWorkerState {
  const supported = typeof navigator !== 'undefined' && 'serviceWorker' in navigator;

  const [registered, setRegistered] = useState(false);
  const [updateReady, setUpdateReady] = useState(false);
  const [reg, setReg] = useState<ServiceWorkerRegistration | null>(null);

  useEffect(() => {
    if (!supported) return;

    navigator.serviceWorker.getRegistration().then((r) => {
      if (!r) return;
      setReg(r);
      setRegistered(true);
      setUpdateReady(!!r.waiting);

      // Listen for a new SW entering the "waiting" state
      r.addEventListener('updatefound', () => {
        const installing = r.installing;
        if (!installing) return;
        installing.addEventListener('statechange', () => {
          if (installing.state === 'installed' && navigator.serviceWorker.controller) {
            setUpdateReady(true);
          }
        });
      });
    });

    // When a new SW takes control, reload the page so users get fresh assets
    let refreshing = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (!refreshing) {
        refreshing = true;
        window.location.reload();
      }
    });
  }, [supported]);

  const checkForUpdate = useCallback(async () => {
    if (!reg) return;
    try {
      await reg.update();
      setUpdateReady(!!reg.waiting);
    } catch {
      // network offline — ignore
    }
  }, [reg]);

  const applyUpdate = useCallback(() => {
    if (!reg?.waiting) return;
    reg.waiting.postMessage({ type: 'SKIP_WAITING' });
    // controllerchange listener above will reload
  }, [reg]);

  const forceUpdate = useCallback(async () => {
    // 1. Unregister all service workers
    const registrations = await navigator.serviceWorker.getRegistrations();
    for (const r of registrations) {
      await r.unregister();
    }
    // 2. Delete every cache
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
    // 3. Hard reload
    window.location.reload();
  }, []);

  return { supported, registered, updateReady, checkForUpdate, applyUpdate, forceUpdate };
}
